var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import buttonStyles from '@cof/fs-button-core/lib/button.lit.min.js';
import circularLoaderStyles from '@cof/fs-circular-loader-core/lib/circular-loader.lit.js';
import { FormControlMixin } from '@cof/fs-form-control';
import { submit } from '@open-wc/form-helpers';
import { css, html, LitElement } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
/**
 * `FsButton` implements the Financial Services extension of the Gravity Design System's button component.
 *
 * Be sure to consult with the design guidelines for the button to ensure proper state usage, it is possible to force the button component into an unsupported state.
 *
 * Currently supported states include:
 *
 * - Primary
 *   - Filled
 *   - Outlined
 *   - Ghost
 *   - Inverted
 *   - Outlined/inverted
 *   - Ghost/inverted
 * - Secondary
 *   - Filled
 *   - Outlined
 *   - Ghost
 *   - Inverted
 * - Action
 *   - Filled
 *   - Inverted
 * - Destructive
 *   - Filled
 *   - Ghost
 *
 * ## Installation
 *
 * ```bash
 * npm i @cof/fs-button-wc
 * ```
 *
 * ## Usage
 *
 * To import the side-effectful registration of `<fs-button>` call
 *
 * ```javascript
 * import '@cof/fs-button-wc/fs-button.js';
 * ```
 *
 * To import the `FsButton` class as either a base class or to self-register the custom element, use
 *
 * ```javascript
 * import { FsButton } from '@cof/fs-button-wc';
 * ```
 *
 * ## Example
 *
 * ```html
 * <fs-button variant="primary">
 *   <span slot="button-icon"><svg><!-- svg markup --></svg></span>
 *   Button text
 * </fs-button>
 * ```
 *
 * @element fs-button
 * @slot - The button text
 * @slot button-icon - The slot for the button's icon if used with a label
 */
export class FsButton extends FormControlMixin(LitElement) {
    constructor() {
        super(...arguments);
        /**
         * If true the button will be compact
         * @attr
         */
        this.compact = false;
        /**
         * Toggles the button's disabled state
         * @attr
         */
        this.disabled = false;
        /**
         * Toggles the button's ghost state
         * @attr
         */
        this.ghost = false;
        /**
         * Toggles the button's inverted state
         * @attr
         */
        this.inverted = false;
        /**
         * When set the true the button will indicate to the user
         * that some long-running task is in process. For accessibility reasons
         * this should only be used if the task is likely to take two seconds
         * or longer in duration
         * @attr
         */
        this.loading = false;
        /**
         * Toggles the button's inverted state
         * @attr
         */
        this.outlined = false;
        /**
         * The proxy for the HTMLButtonElement's type property
         * @attr
         */
        this.type = null;
        /**
         * The button's variant. Can be primary, secondary, action or destructive
         * @attr
         */
        this.variant = 'primary';
        /**
         * This set of properties will be for ARIA delegation until AutoARIAMixin is implemented.
         * This set of APIs could be abstracted into a separate controller or mixin, but due to the
         * fact that idrefs cannot be passed between roots behavior would be limited so will be treated
         * on an ad hoc basis.
         *
         * TODO: Migrate to AutoARIAMixin or equivalent when available
         */
        /**
         * Forward the contents of this property to the aria-label of the internal button element.
         * As an accessibility best practice, this should probably not be relied upon but is available
         * if absolutely necessary.
         * @attribute delegated-aria-label
         */
        this.delegatedAriaLabel = undefined;
        /**
         * Override an over-eager method from the original FormControlMixin
         * @private
         */
        this._onKeypressSubmit = () => {
            return;
        };
    }
    static get styles() {
        return [
            css `
      :host { display: inline-block; }
      button { width: 100%; }
      ::slotted([slot="button-icon"]) { display: block; }
    `,
            buttonStyles,
            circularLoaderStyles
        ];
    }
    render() {
        return html `<button class="fs-button fs-button--${this.variant} ${this.compact ? 'fs-button--compact' : ''} ${this.ghost ? 'fs-button--ghost' : ''} ${this.inverted ? 'fs-button--inverted' : ''} ${this.outlined ? 'fs-button--outlined' : ''} ${this.iconPosition === 'center' ? 'fs-button--icon-only' : ''} ${this.loading ? 'fs-button--loading' : ''}" type="${this.type}" ?disabled="${this.disabled}" @click="${this.click}" style="${this.loading ? `width: ${this._defaultWidth}` : ''}" aria-disabled="${this.loading}" .ariaLabel="${ifDefined(this.loading ? 'loading' : this.delegatedAriaLabel)}">
      ${this.loading
            ? html `
        <svg
          viewBox="0 0 100 100"
          xmlns="http://www.w3.org/2000/svg"
          role="progressbar"
          class="fs-circular-loader fs-circular-loader--indeterminate fs-circular-loader--inherit-color ${this.compact ? 'fs-circular-loader--small' : 'fs-circular-loader--medium'}"
        >
          <circle
            class="fs-circular-loader__indicator"
            cx="50"
            cy="50"
            r="45"
          ></circle>
        </svg>
      `
            : html `
        ${this.iconPosition === 'leading'
                ? html `
          <span class="fs-button__icon fs-button__icon--leading"><slot name="button-icon"></slot></span>
        `
                : ''}
        <slot></slot>
        ${this.iconPosition === 'trailing'
                ? html `<span class="fs-button__icon fs-button__icon--trailing"><slot name="button-icon"></slot></span>`
                : ''}
      `}
    </button>`;
    }
    firstUpdated() {
        this.linkControl(this.button);
        document.fonts.ready.then(() => {
            this._cacheWidth();
        });
    }
    focus() {
        super.focus();
        this.button.focus();
    }
    /**
     * Called when the button is clicked, this method will trigger
     * the proper events on a parent form
     */
    click() {
        if (this.disabled) {
            return;
        }
        if ((this.form && this.type === 'submit') || (this.form && this.type === null)) {
            submit(this.form);
        }
        else if (this.form && this.type === 'reset') {
            this.form.reset();
        }
    }
    /**
     * Cache the width of the button in case it gets toggled to a loading state
     * this will happen on slotchange, on document.fonts.ready and on a toggle for compact
     */
    _cacheWidth() {
        this._defaultWidth = window.getComputedStyle(this.button).width;
    }
    /** If compact updates, cache the new width */
    updated(changed) {
        super.updated(changed);
        if (changed.has('compact')) {
            this._cacheWidth();
        }
    }
}
/** The element's canonical name for use with safe define */
FsButton.canonicalName = 'button';
__decorate([
    property({ type: Boolean, reflect: true })
], FsButton.prototype, "compact", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsButton.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsButton.prototype, "ghost", void 0);
__decorate([
    property({ type: String, attribute: 'icon-position' })
], FsButton.prototype, "iconPosition", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsButton.prototype, "inverted", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsButton.prototype, "loading", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsButton.prototype, "outlined", void 0);
__decorate([
    property({ type: String, reflect: true })
], FsButton.prototype, "type", void 0);
__decorate([
    property({ type: String, reflect: true })
], FsButton.prototype, "variant", void 0);
__decorate([
    query('button')
], FsButton.prototype, "button", void 0);
__decorate([
    state()
], FsButton.prototype, "_defaultWidth", void 0);
__decorate([
    property({ type: String, attribute: 'delegated-aria-label' })
], FsButton.prototype, "delegatedAriaLabel", void 0);
//# sourceMappingURL=Button.js.map